import React, { useMemo } from 'react';

import i18n from 'i18n-js';

import MapEditorCanvas from 'components/tableMaps/CreateTableMapWizard/components/MapEditorCanvas';
import TableFigure from 'components/tableMaps/CreateTableMapWizard/components/TableFigure';
import {
    computeRoom,
    tableFootprintPx,
} from 'components/tableMaps/CreateTableMapWizard/mapGeometry';
import { tableChairs } from 'components/tableMaps/CreateTableMapWizard/tableChairs';
import { theme } from 'config/theme';
import { TableMapDetails } from 'store/tableMapsStore/reducer';
import { isReservableTable } from 'helpers/reservationHelper';

export const TABLE_STATUS_COLORS = {
    free: theme.palette.success.main,
    occupied: theme.palette.warning.main,
    booked: theme.palette.error.light,
};

const HALO_PAD = 8;
const HALO_RADIUS = 10;
const HALO_BORDER = 3;
const PILL_HEIGHT = 22;
const PILL_GAP = 6;
const PILL_FONT_SIZE = 9;
const PILL_CHAR_WIDTH = 5.8;
const PILL_PADDING = 20;

interface ReservationTableMapProps {
    map: TableMapDetails;
    unavailableTables: Record<string, string | null>;
    selectedTableIds: string[];
    onToggleTable: (tableId: string) => void;
}

function StatusPill({ y, text, color }: { y: number; text: string; color: string }) {
    const width = text.length * PILL_CHAR_WIDTH + PILL_PADDING;
    return (
        <g pointerEvents="none">
            <rect
                x={-width / 2}
                y={y}
                width={width}
                height={PILL_HEIGHT}
                rx={PILL_HEIGHT / 2}
                fill={color}
            />
            <text
                y={y + PILL_HEIGHT / 2}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={PILL_FONT_SIZE}
                fontWeight={700}
                fill={theme.palette.common.white}
                fontFamily={theme.typography.fontFamily}
            >
                {text}
            </text>
        </g>
    );
}

export default function ReservationTableMap({
    map,
    unavailableTables,
    selectedTableIds,
    onToggleTable,
}: ReservationTableMapProps) {
    const room = useMemo(() => computeRoom(map.size), [map.size.width, map.size.height]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <MapEditorCanvas
            gridEnabled={false}
            zoom={1}
            size={map.size}
            area={map.area}
            openings={map.entrances}
            features={map.features}
        >
            {map.tables.filter(isReservableTable).map((table) => {
                const { width, height } = tableFootprintPx(
                    table.shape,
                    table.width,
                    table.height,
                    map.tableDefaults,
                    room
                );
                const chairs = tableChairs(width, height, table.seats, table.shape);
                const halfWidth =
                    Math.max(width / 2, ...chairs.map((c) => Math.abs(c.x) + c.half)) + HALO_PAD;
                const halfHeight =
                    Math.max(height / 2, ...chairs.map((c) => Math.abs(c.y) + c.half)) + HALO_PAD;
                const angle = (table.rotation * Math.PI) / 180;
                const pillY =
                    Math.abs(halfWidth * Math.sin(angle)) +
                    Math.abs(halfHeight * Math.cos(angle)) +
                    PILL_GAP;

                const isUnavailable = table.id in unavailableTables;
                const bookedTime = unavailableTables[table.id];
                const isBooked = isUnavailable && bookedTime !== null;
                const isSelected = !isUnavailable && selectedTableIds.includes(table.id);
                const color = isBooked
                    ? TABLE_STATUS_COLORS.booked
                    : isUnavailable
                    ? TABLE_STATUS_COLORS.occupied
                    : TABLE_STATUS_COLORS.free;

                return (
                    <g
                        key={table.id}
                        transform={`translate(${room.x + table.x * room.width}, ${
                            room.y + table.y * room.height
                        })`}
                        style={{ cursor: isUnavailable ? 'not-allowed' : 'pointer' }}
                        onClick={isUnavailable ? undefined : () => onToggleTable(table.id)}
                    >
                        {isSelected && (
                            <rect
                                x={-halfWidth}
                                y={-halfHeight}
                                width={halfWidth * 2}
                                height={halfHeight * 2}
                                rx={HALO_RADIUS}
                                transform={`rotate(${table.rotation})`}
                                fill={theme.palette.warning.light}
                                stroke={theme.palette.primary.main}
                                strokeWidth={HALO_BORDER}
                            />
                        )}
                        <TableFigure
                            width={width}
                            height={height}
                            seats={table.seats}
                            shape={table.shape}
                            label={table.label}
                            rotation={table.rotation}
                            color={color}
                        />
                        {isBooked && (
                            <StatusPill
                                y={pillY}
                                text={i18n.t('reservations.bookedAt', { time: bookedTime })}
                                color={TABLE_STATUS_COLORS.booked}
                            />
                        )}
                        {isSelected && (
                            <StatusPill
                                y={pillY}
                                text={i18n.t('reservations.selected')}
                                color={theme.palette.primary.main}
                            />
                        )}
                    </g>
                );
            })}
        </MapEditorCanvas>
    );
}
